import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Lozenge from '@atlaskit/lozenge';
import ModalDialog, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Select, { type CSSObjectWithLabel, type ValueType } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { Label } from '@databases/form/FormComponents';
import { AiSummaryIcon } from '@databases/icons/AiSummaryIcon';
import { messages } from '@databases/intl/messages';
import {
  type AiSummaryFieldConfig,
  type AiSummaryOutputFormat
} from '@databases/types/database';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

type FormatOption = { label: string; value: AiSummaryOutputFormat };
type PreviewOption = { label: string; value: string };

const EMPTY_PREVIEW_OPTIONS: PreviewOption[] = [];

export interface AiSummaryFieldModalProps {
  initialConfig?: AiSummaryFieldConfig;
  onClose: () => void;
  onRegenerate?: (format: AiSummaryOutputFormat) => void;
  onSaveAndRun?: (config: AiSummaryFieldConfig) => void;
  onSaveWithoutRunning?: (config: AiSummaryFieldConfig) => void;
}

const contentStyles = xcss({
  height: '389px'
});

const layoutStyles = xcss({
  columnGap: 'space.300',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  height: '100%'
});

const leftColumnStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  padding: 'space.300'
});

const rightColumnStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  padding: 'space.300'
});

const panelRadiusStyle = {
  borderRadius: token('radius.small', '4px')
};

const previewControlsStyles = xcss({
  width: '100%'
});

const previewSelectWrapperStyles = xcss({
  flexGrow: 1,
  minWidth: '0px'
});

const compactSelectStyles = {
  control: (base: CSSObjectWithLabel, state: { isFocused: boolean }) => ({
    ...base,
    border: `1px solid ${
      state.isFocused ? token('color.border.focused') : token('color.border')
    }`,
    borderRadius: token('radius.medium', '6px'),
    height: '32px',
    minHeight: '32px'
  }),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.150')}`
  }),
  indicatorsContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    height: '100%'
  }),
  dropdownIndicator: (base: CSSObjectWithLabel) => ({
    ...base,
    padding: `${token('space.0')} ${token('space.100')}`
  })
};

const AiSummaryFieldModal: FunctionComponent<AiSummaryFieldModalProps> = ({
  initialConfig,
  onClose,
  onRegenerate,
  onSaveAndRun,
  onSaveWithoutRunning
}) => {
  const { formatMessage } = useIntl();
  const [format, setFormat] = useState<AiSummaryOutputFormat>(
    initialConfig?.outputFormat ?? 'paragraph'
  );

  const formatOptions = useMemo<FormatOption[]>(
    () => [
      {
        label: formatMessage(messages['field-settings.ai-summary.format-one-liner']),
        value: 'one-liner'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-bullet-points']),
        value: 'bullet-points'
      },
      {
        label: formatMessage(messages['field-settings.ai-summary.format-paragraph']),
        value: 'paragraph'
      }
    ],
    [formatMessage]
  );

  const config = useMemo<AiSummaryFieldConfig>(
    () => ({
      outputFormat: format
    }),
    [format]
  );

  const handleFormatChange = useCallback((option: ValueType<FormatOption, false>) => {
    if (option) {
      setFormat(option.value);
    }
  }, []);

  const handleRegenerate = useCallback(() => {
    onRegenerate?.(format);
  }, [format, onRegenerate]);

  const handleSaveWithoutRunning = useCallback(() => {
    onSaveWithoutRunning?.(config);
  }, [config, onSaveWithoutRunning]);

  const handleSaveAndRun = useCallback(() => {
    onSaveAndRun?.(config);
  }, [config, onSaveAndRun]);

  return (
    <ModalDialog
      width={968}
      onClose={onClose}
      shouldScrollInViewport
      testId="ai-summary-field-modal"
    >
      <ModalHeader hasCloseButton>
        <ModalTitle>
          <Inline alignBlock="center" space="space.100">
            <AiSummaryIcon label="" testId="ai-summary-field-icon" />
            <Heading as="h1" size="medium">
              {formatMessage(messages['field-settings.ai-summary.modal-title'])}
            </Heading>
            <IconButton
              icon={InformationCircleIcon}
              appearance="subtle"
              spacing="compact"
              label={formatMessage(
                messages['field-settings.ai-summary.modal-description']
              )}
              isTooltipDisabled={false}
              tooltip={{ position: 'bottom' }}
              testId="ai-summary-field-info"
            />
          </Inline>
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Box xcss={contentStyles}>
          <Box xcss={layoutStyles}>
            <Box xcss={leftColumnStyles} style={panelRadiusStyle}>
              <Stack space="space.250">
                <Heading as="h2" size="xsmall">
                  {formatMessage(
                    messages['field-settings.ai-summary.field-configuration']
                  )}
                </Heading>

                <Stack space="space.100">
                  <Label id="ai-summary-format-label" htmlFor="ai-summary-format">
                    <Text color="color.text.subtle" size="small" weight="bold">
                      {formatMessage(messages['field-settings.ai-summary.format'])}
                    </Text>
                  </Label>
                  <Select<FormatOption>
                    inputId="ai-summary-format"
                    options={formatOptions}
                    value={
                      formatOptions.find((option) => option.value === format) ?? null
                    }
                    onChange={handleFormatChange}
                    isSearchable={false}
                    styles={compactSelectStyles}
                  />
                </Stack>
                <Stack space="space.100">
                  <Inline alignBlock="center" space="space.050">
                    <Label
                      id="ai-summary-auto-apply-label"
                      htmlFor="ai-summary-auto-apply"
                    >
                      <Text color="color.text.disabled" size="small" weight="bold">
                        {formatMessage(messages['field-settings.ai-summary.auto-apply'])}
                      </Text>
                    </Label>
                    <Lozenge
                      appearance="default"
                      isBold={false}
                      spacing="spacious"
                      style={{ backgroundColor: token('color.background.neutral') }}
                      testId="ai-summary-coming-soon"
                    >
                      {formatMessage(messages['field-settings.ai-summary.coming-soon'])}
                    </Lozenge>
                  </Inline>
                  <Stack space="space.050">
                    <Checkbox
                      isDisabled
                      label={formatMessage(
                        messages['field-settings.ai-summary.apply-to-new-entries']
                      )}
                    />
                    <Checkbox
                      isDisabled
                      label={formatMessage(
                        messages['field-settings.ai-summary.apply-when-entry-edited']
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Box xcss={rightColumnStyles} style={panelRadiusStyle}>
              <Stack space="space.250">
                <Heading as="h2" size="xsmall">
                  {formatMessage(messages['field-settings.ai-summary.live-preview'])}
                </Heading>
                <Stack space="space.100">
                  <Label
                    id="ai-summary-preview-with-label"
                    htmlFor="ai-summary-preview-with"
                  >
                    <Text color="color.text.subtle" size="small" weight="bold">
                      {formatMessage(messages['field-settings.ai-summary.preview-with'])}
                    </Text>
                  </Label>
                  <Inline
                    alignBlock="center"
                    space="space.100"
                    xcss={previewControlsStyles}
                  >
                    <Box xcss={previewSelectWrapperStyles}>
                      <Select<PreviewOption>
                        inputId="ai-summary-preview-with"
                        options={EMPTY_PREVIEW_OPTIONS}
                        value={null}
                        isSearchable={false}
                        styles={compactSelectStyles}
                      />
                    </Box>
                    <Button
                      onClick={handleRegenerate}
                      style={{
                        borderRadius: token('radius.medium', '6px'),
                        color: token('color.text.subtle')
                      }}
                      testId="ai-summary-regenerate"
                    >
                      {formatMessage(messages['field-settings.ai-summary.regenerate'])}
                    </Button>
                  </Inline>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Inline space="space.100" alignBlock="center">
          <Button
            appearance="subtle"
            onClick={handleSaveWithoutRunning}
            testId="ai-summary-save-without-running"
          >
            {formatMessage(messages['field-settings.ai-summary.save-without-running'])}
          </Button>
          <Button
            appearance="primary"
            onClick={handleSaveAndRun}
            testId="ai-summary-save-and-run"
          >
            {formatMessage(messages['field-settings.ai-summary.save-and-run'])}
          </Button>
        </Inline>
      </ModalFooter>
    </ModalDialog>
  );
};

export default AiSummaryFieldModal;
